/**
 * @coeldery/form-text-field
 * 商戶管理 — 單行文字欄位（含標籤、提示、錯誤）
 *
 * 規格（.coappery/merchant_platform.md §2；rules.md §1）：
 *   - 標籤在上、輸入框高 44px（長者／手指友善）、錯誤文字用 var(--color-accent)
 *   - 顏色只用 CSS var；文字全部 via i18n t('key')
 *   - 純受控組件：值與 onChange 由 props 傳入，模組不持有任何業務狀態
 *   - 空字串一律轉為 null（DB 用 NULL 表示「未填」）
 *
 * 衍生自：src/ui.ts 既有樣式（input／inputError／label／errText／hint）；
 *        原 MerchantForm.tsx 內 field() 的 id 命名（f-<key>）保持不變，方便自動測試。
 */
import { useTranslation } from 'react-i18next'
import { input, inputError, label, errText, hint } from '../ui-kit'

export interface FormTextFieldProps {
  /** 欄位 id（同時用於 label htmlFor 與自動測試選擇器）*/
  id: string
  /** 標籤 i18n key */
  labelKey: string
  /** 目前值；null＝未填 */
  value: string | null
  /** 值改變（空字串會傳 null）*/
  onChange: (v: string | null) => void
  /** input type（text／date／number⋯）*/
  type?: string
  placeholder?: string
  /** 提示文字 i18n key（標籤下方灰色小字）*/
  hintKey?: string
  /** 錯誤文字（已本地化的字串；由驗證層產生）*/
  error?: string
  /** 唯讀（例如建立後不可改的商戶 ID）*/
  readOnly?: boolean
}

export default function FormTextField({
  id, labelKey, value, onChange, type = 'text', placeholder, hintKey, error, readOnly,
}: FormTextFieldProps) {
  const { t } = useTranslation()
  return (
    <div>
      <label style={label} htmlFor={id}>{t(labelKey)}</label>
      <input
        id={id}
        type={type}
        style={error ? inputError : input}
        placeholder={placeholder}
        readOnly={readOnly}
        aria-invalid={error ? true : undefined}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      />
      {hintKey && <p style={hint}>{t(hintKey)}</p>}
      {error && <p style={errText}>{error}</p>}
    </div>
  )
}
